import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  currencyConfigurationService,
  CurrencyConfigurationData,
  CurrencyConfigurationCreateData,
  CurrencyConfigurationUpdateData,
  ExchangeRateUpdate
} from '@/services/billing/currency-configuration.service';
import { toast } from 'sonner';

// Transform backend data to frontend format
function transformCurrencyData(data: CurrencyConfigurationData): CurrencyConfigurationData {
  // Handle any snake_case to camelCase conversion if needed
  // Backend uses is_primary in responses but is_default in requests
  const isPrimary = data.isPrimary ?? data.is_primary ?? data.isDefault ?? data.is_default ?? false;
  
  return {
    id: data.id,
    currencyCode: data.currencyCode || data.currency_code || '',
    displayName: data.displayName || data.display_name,
    symbol: data.symbol || '',
    symbolPosition: data.symbolPosition || data.symbol_position || 'before',
    decimalPlaces: data.decimalPlaces ?? data.decimal_places ?? 2,
    thousandSeparator: data.thousandSeparator || data.thousand_separator || ',',
    decimalSeparator: data.decimalSeparator || data.decimal_separator || '.',
    roundingMode: data.roundingMode || data.rounding_mode || 'round',
    exchangeRate: data.exchangeRate ?? data.exchange_rate ?? 1,
    isPrimary: isPrimary,
    isDefault: isPrimary, // Keep both for compatibility
    isActive: data.isActive ?? data.is_active ?? true,
    showInSelector: data.showInSelector ?? data.show_in_selector ?? true,
    rateUpdatedAt: data.rateUpdatedAt || data.rate_updated_at,
    createdAt: data.createdAt || data.created_at,
    updatedAt: data.updatedAt || data.updated_at
  };
}

// Query Keys
export const currencyConfigurationKeys = {
  all: ['currency-configurations'] as const,
  lists: () => [...currencyConfigurationKeys.all, 'list'] as const,
  list: (productId: string) => [...currencyConfigurationKeys.lists(), productId] as const,
  details: () => [...currencyConfigurationKeys.all, 'detail'] as const,
  detail: (productId: string, currencyCode: string) => 
    [...currencyConfigurationKeys.details(), productId, currencyCode] as const,
};

/**
 * Hook to fetch all currency configurations for a product
 */
export function useCurrencyConfigurations(productId: string) {
  return useQuery({
    queryKey: currencyConfigurationKeys.list(productId),
    queryFn: async () => {
      try {
        const data = await currencyConfigurationService.getCurrencyConfigurations(productId);
        console.log('useCurrencyConfigurations - Raw data from service:', data);
        // Transform each currency configuration
        const transformed = data.map(transformCurrencyData);
        console.log('useCurrencyConfigurations - Transformed data:', transformed);
        return transformed;
      } catch (error) {
        console.warn('Failed to fetch currency configurations, using defaults', error);
        // Return empty array as fallback
        return [];
      }
    },
    enabled: !!productId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 1, // Only retry once to avoid multiple 401/404 errors
  });
}

/**
 * Hook to fetch a specific currency configuration
 */
export function useCurrencyConfiguration(productId: string, currencyCode: string) {
  return useQuery({
    queryKey: currencyConfigurationKeys.detail(productId, currencyCode),
    queryFn: async () => {
      const data = await currencyConfigurationService.getCurrencyConfiguration(productId, currencyCode);
      return transformCurrencyData(data);
    },
    enabled: !!productId && !!currencyCode,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

/**
 * Hook to add a new currency configuration
 */
export function useAddCurrencyConfiguration() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ productId, data }: { productId: string; data: CurrencyConfigurationCreateData }) =>
      currencyConfigurationService.createCurrencyConfiguration(productId, data),
    onSuccess: (newConfig, { productId }) => {
      console.log('Currency created, response:', newConfig);
      
      // Invalidate and refetch the list to get fresh data from backend
      queryClient.invalidateQueries({ 
        queryKey: currencyConfigurationKeys.list(productId),
        exact: true 
      });
      
      // Also invalidate the general lists
      queryClient.invalidateQueries({ 
        queryKey: currencyConfigurationKeys.lists() 
      });
      
      toast.success('Currency configuration added successfully');
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Failed to add currency configuration';
      toast.error(message);
    },
  });
}

/**
 * Hook to update a currency configuration
 */
export function useUpdateCurrencyConfiguration() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ 
      productId, 
      currencyCode, 
      data 
    }: { 
      productId: string; 
      currencyCode: string; 
      data: CurrencyConfigurationUpdateData 
    }) =>
      currencyConfigurationService.updateCurrencyConfiguration(productId, currencyCode, data),
    onSuccess: (updatedConfig, { productId, currencyCode }) => {
      const transformedConfig = transformCurrencyData(updatedConfig);
      
      // Update the detail cache
      queryClient.setQueryData(
        currencyConfigurationKeys.detail(productId, currencyCode),
        transformedConfig
      );
      
      // Update the configuration in list cache
      queryClient.setQueryData(
        currencyConfigurationKeys.list(productId),
        (oldData: CurrencyConfigurationData[] | undefined) => {
          if (!oldData) return [transformedConfig];
          return oldData.map(config => 
            config.currencyCode === currencyCode ? transformedConfig : config
          );
        }
      );
      
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: currencyConfigurationKeys.lists() });
      
      toast.success('Currency configuration updated successfully');
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Failed to update currency configuration';
      toast.error(message);
    },
  });
}

/**
 * Hook to delete a currency configuration
 */
export function useDeleteCurrencyConfiguration() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ productId, currencyCode }: { productId: string; currencyCode: string }) =>
      currencyConfigurationService.deleteCurrencyConfiguration(productId, currencyCode),
    onSuccess: (_, { productId, currencyCode }) => {
      // Remove from list cache
      queryClient.setQueryData(
        currencyConfigurationKeys.list(productId),
        (oldData: CurrencyConfigurationData[] | undefined) => {
          if (!oldData) return [];
          return oldData.filter(config => config.currencyCode !== currencyCode);
        }
      );
      
      // Remove detail cache
      queryClient.removeQueries({ 
        queryKey: currencyConfigurationKeys.detail(productId, currencyCode) 
      });
      
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: currencyConfigurationKeys.lists() });
      
      toast.success('Currency configuration removed successfully');
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Failed to remove currency configuration';
      toast.error(message);
    },
  });
}

/**
 * Hook to bulk update exchange rates
 */
export function useUpdateExchangeRates() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ productId, rates }: { productId: string; rates: ExchangeRateUpdate[] }) =>
      currencyConfigurationService.updateExchangeRates(productId, rates),
    onSuccess: (updatedConfigs, { productId }) => {
      const transformedConfigs = updatedConfigs.map(transformCurrencyData);
      
      // Update the list cache with new exchange rates
      queryClient.setQueryData(
        currencyConfigurationKeys.list(productId),
        transformedConfigs
      );
      
      // Update individual detail caches
      transformedConfigs.forEach(config => {
        queryClient.setQueryData(
          currencyConfigurationKeys.detail(productId, config.currencyCode),
          config
        );
      });
      
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: currencyConfigurationKeys.lists() });
      
      toast.success('Exchange rates updated successfully');
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Failed to update exchange rates';
      toast.error(message);
    },
  });
}

/**
 * Hook to set primary currency
 */
export function useSetPrimaryCurrency() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ productId, currencyCode }: { productId: string; currencyCode: string }) =>
      currencyConfigurationService.setPrimaryCurrency(productId, currencyCode),
    onSuccess: (updatedConfig, { productId, currencyCode }) => {
      const transformedConfig = transformCurrencyData(updatedConfig);
      
      // Update the list cache - set new primary and unset others
      queryClient.setQueryData(
        currencyConfigurationKeys.list(productId),
        (oldData: CurrencyConfigurationData[] | undefined) => {
          if (!oldData) return [transformedConfig];
          return oldData.map(config => ({
            ...config,
            isPrimary: config.currencyCode === currencyCode,
            isDefault: config.currencyCode === currencyCode
          }));
        }
      );
      
      // Update detail cache
      queryClient.setQueryData(
        currencyConfigurationKeys.detail(productId, currencyCode),
        transformedConfig
      );
      
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: currencyConfigurationKeys.lists() });
      
      toast.success(`${currencyCode} set as primary currency`);
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Failed to set primary currency';
      toast.error(message);
    },
  });
}

/**
 * Hook to toggle currency active status
 */
export function useToggleCurrencyStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ 
      productId, 
      currencyCode, 
      isActive 
    }: { 
      productId: string; 
      currencyCode: string; 
      isActive: boolean 
    }) =>
      currencyConfigurationService.toggleCurrencyStatus(productId, currencyCode, isActive),
    onSuccess: (updatedConfig, { productId, currencyCode }) => {
      const transformedConfig = transformCurrencyData(updatedConfig);
      
      // Update the detail cache
      queryClient.setQueryData(
        currencyConfigurationKeys.detail(productId, currencyCode),
        transformedConfig
      );
      
      // Update the configuration in list cache
      queryClient.setQueryData(
        currencyConfigurationKeys.list(productId),
        (oldData: CurrencyConfigurationData[] | undefined) => {
          if (!oldData) return [transformedConfig];
          return oldData.map(config => 
            config.currencyCode === currencyCode ? transformedConfig : config
          );
        }
      );
      
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: currencyConfigurationKeys.lists() });
      
      const status = updatedConfig.isActive ? 'activated' : 'deactivated';
      toast.success(`${currencyCode} ${status} successfully`);
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Failed to toggle currency status';
      toast.error(message);
    },
  });
}

/**
 * Get active currencies from configurations
 */
export function useActiveCurrencies(productId: string): string[] {
  const { data: configurations = [] } = useCurrencyConfigurations(productId);
  return configurations
    .filter(config => config.isActive)
    .map(config => config.currencyCode);
}

/**
 * Get primary currency from configurations
 */
export function usePrimaryCurrency(productId: string): string {
  const { data: configurations = [] } = useCurrencyConfigurations(productId);
  const primaryConfig = configurations.find(config => config.isPrimary || config.isDefault);
  return primaryConfig?.currencyCode || 'USD';
}

/**
 * Prefetch currency configurations for hover/preview scenarios
 */
export function usePrefetchCurrencyConfigurations() {
  const queryClient = useQueryClient();

  return (productId: string) => {
    queryClient.prefetchQuery({
      queryKey: currencyConfigurationKeys.list(productId),
      queryFn: () => currencyConfigurationService.getCurrencyConfigurations(productId),
      staleTime: 5 * 60 * 1000,
    });
  };
}